import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PenLine, Trash2, Upload } from 'lucide-react';
import { api } from '../../services/api';
import DetailSection from '../details/DetailSection';
import { FormServerError } from '../ui/Field';
import ConfirmDialog from '../ui/ConfirmDialog';
import SignatureCanvas, { type SignatureCanvasHandle } from './SignatureCanvas';
import { formatDateTime } from '../../utils/format';

/**
 * FIX-D337 stage א (D-337 §2) - "החתימה שלי" in the profile: drawn once (or an uploaded PNG), used by the OTE manager
 * to sign documents before they go to the customer.
 */
export default function MySignaturePanel() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const pad = useRef<SignatureCanvasHandle>(null);
  const file = useRef<HTMLInputElement>(null);
  const [drawing, setDrawing] = useState(false);
  const [empty, setEmpty] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const query = useQuery({ queryKey: ['my-signature'], queryFn: async () => (await api.getMySignature()).data });
  const save = useMutation({
    mutationFn: ({ image, source }: { image: string; source: 'draw' | 'upload' }) => api.saveMySignature(image, source),
    onSuccess: () => { setDrawing(false); qc.invalidateQueries({ queryKey: ['my-signature'] }); },
  });
  const remove = useMutation({
    mutationFn: () => api.deleteMySignature(),
    onSuccess: () => { setConfirmDelete(false); qc.invalidateQueries({ queryKey: ['my-signature'] }); },
  });
  const sig = query.data;

  const onFile = (f: File | undefined) => {
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        // any picture -> PNG (the server accepts PNG only)
        const c = document.createElement('canvas');
        const scale = Math.min(1, 900 / img.width, 300 / img.height);
        c.width = Math.max(1, Math.round(img.width * scale));
        c.height = Math.max(1, Math.round(img.height * scale));
        c.getContext('2d')?.drawImage(img, 0, 0, c.width, c.height);
        save.mutate({ image: c.toDataURL('image/png'), source: 'upload' });
      };
      img.src = String(reader.result);
    };
    reader.readAsDataURL(f);
  };

  return (
    <DetailSection
      title={t('docReview.signature.title')}
      description={t('docReview.signature.hint')}
      actions={!drawing && (
        <>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setDrawing(true); setEmpty(true); }}>
            <PenLine size={16} aria-hidden="true" /> {sig?.has_signature ? t('docReview.signature.redraw') : t('docReview.signature.draw')}
          </button>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => file.current?.click()} disabled={save.isPending}>
            <Upload size={16} aria-hidden="true" /> {t('docReview.signature.upload')}
          </button>
          <input ref={file} type="file" accept="image/png,image/jpeg" className="sr-only" tabIndex={-1} aria-hidden="true"
            onChange={(e) => { onFile(e.target.files?.[0]); e.target.value = ''; }} />
        </>
      )}
    >
      {drawing ? (
        <div className="space-y-3">
          <SignatureCanvas ref={pad} onChange={setEmpty} />
          <div className="flex flex-wrap gap-2">
            <button type="button" className="btn btn-primary btn-sm" disabled={empty || save.isPending}
              onClick={() => { const png = pad.current?.toPng(); if (png) save.mutate({ image: png, source: 'draw' }); }}>
              {t('docReview.signature.save')}
            </button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => pad.current?.clear()}>{t('docReview.signature.clear')}</button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setDrawing(false)}>{t('common.cancel')}</button>
          </div>
        </div>
      ) : sig?.has_signature && sig.image ? (
        <div className="flex flex-wrap items-end gap-4">
          <img src={sig.image} alt={t('docReview.signature.saved')} className="h-24 max-w-full rounded-lg border border-surface-200 bg-surface-0 p-2" />
          <div className="text-xs text-surface-600">
            {t('docReview.signature.savedAt', { when: sig.updated_at ? formatDateTime(sig.updated_at) : '' })}
            <button type="button" className="btn btn-ghost btn-sm ms-2 text-danger-700" onClick={() => setConfirmDelete(true)}>
              <Trash2 size={14} aria-hidden="true" /> {t('docReview.signature.remove')}
            </button>
          </div>
        </div>
      ) : (
        <p className="text-sm text-surface-600">{t('docReview.signature.none')}</p>
      )}
      <FormServerError error={save.error || remove.error} fallback={t('docReview.signature.saveError')} className="mt-3" />
      <ConfirmDialog
        open={confirmDelete}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={() => remove.mutate()}
        title={t('docReview.signature.removeTitle')}
        confirmLabel={t('docReview.signature.remove')}
        danger
        loading={remove.isPending}
      />
    </DetailSection>
  );
}
